import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Row, Col, Card, Select, InputNumber, Space, Table, Alert, Spin, Tag, Switch,
  Tooltip, Button,
} from 'antd';
import dayjs from 'dayjs';
import api from '../../libs/api';
import { computeShForecast, aggregateByPeriod } from '../../utils/financialPlanning';
import { NEUTRAL } from './shared';

/**
 * ShineHub Residential's gross profit model.
 *
 * The same engine Financial Planning runs, with the parts that do not belong on
 * a cashflow page removed: opening balances, the balance sheet and cashflow
 * output tables, and — deliberately — the start month and horizon selectors.
 * Those last two now come from Cashflow Control itself, because a model whose
 * window can be set independently of the page it feeds is a model that will
 * eventually disagree with it.
 *
 * Revenue is recognised at installation, not at sale, so sales and installs are
 * separate drivers and the closing-to-install lag decides which month a signed
 * contract lands in. That lag is the whole reason this is a pipeline model
 * rather than a monthly percentage.
 *
 * Reports its result upward rather than drawing the output table itself: the
 * eight-column comparison against Xero belongs to the page, so that actual and
 * forecast are laid out by one thing rather than two.
 */

const moneyFmt = (v) => {
  if (v == null || v === '') return '';
  const num = parseFloat(v);
  if (Number.isNaN(num)) return String(v);
  const parts = (Math.round(num * 100) / 100).toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `$ ${parts.join('.')}`;
};
const moneyParse = (v) => v.replace(/\$\s?|,/g, '');

const TYPES = ['Upfront', 'PPA', 'BNPL'];

/** One month's business lines added back to a total. */
function sumLines(byLine) {
  if (!byLine) return null;
  const t = Object.values(byLine).reduce((acc, c) => ({
    revenue: acc.revenue + c.revenue,
    cogs: acc.cogs + c.cogs,
  }), { revenue: 0, cogs: 0 });
  return {
    revenue: Math.round(t.revenue * 100) / 100,
    cogs: Math.round(t.cogs * 100) / 100,
    gp: Math.round((t.revenue - t.cogs) * 100) / 100,
  };
}
const typeColour = (t) => (t === 'Upfront' ? 'green' : (t === 'PPA' ? 'blue' : 'orange'));

export default function ShGpModel({
  startMonth, months, saved, onForecast, renderSections, onOpenScenarios, loadRequest,
}) {
  const horizonMonths = months.length;

  const [baseline, setBaseline] = useState(null);
  const [baselineLoading, setBaselineLoading] = useState(false);
  const [baselineError, setBaselineError] = useState(null);

  const [pipeline, setPipeline] = useState([]);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [monthsBack, setMonthsBack] = useState(6);
  const [includeExisting, setIncludeExisting] = useState(true);

  const [byType, setByType] = useState({
    Upfront: { avgRevenue: 0, avgCrmPayment: 0, cogsPct: 0.75 },
    PPA: { avgRevenue: 0, avgCrmPayment: 0, cogsPct: 0.75 },
    BNPL: { avgRevenue: 0, avgCrmPayment: 0, cogsPct: 0.75 },
  });
  const [avgClosingToInstallDays, setAvgClosingToInstallDays] = useState(90);
  const [drivers, setDrivers] = useState(months.map(() => ({ totalSales: 0, totalInstalls: 0 })));

  /**
   * Whether a person has changed anything yet.
   *
   * Everything below fills itself in until they have, and nothing overwrites
   * them afterwards. A ref-per-source guard was tried and produced a race: two
   * fetches both claiming "I have seeded", whichever landed first winning, and
   * the loser's fields left blank.
   */
  const [touched, setTouched] = useState(false);

  const touchedRef = useRef(false);

  /**
   * Mark the model touched, synchronously.
   *
   * The state flag alone is not enough to guard the seeding effects. Effects in
   * one commit run in declaration order and all share the render's closure, so
   * an effect that sets `touched` does not stop a later one from reading it as
   * false and seeding straight over what the first just applied. That is
   * exactly what happened to "load v1": the scenario landed, and the
   * saved-budget effect put the newest version back on top of it in the same
   * tick. The ref changes the instant it is set, so ordering stops mattering.
   */
  const markTouched = () => {
    touchedRef.current = true;
    setTouched(true);
  };

  const updateType = (type, key, val) => {
    markTouched();
    setByType((p) => ({ ...p, [type]: { ...p[type], [key]: val } }));
  };
  const updateDriver = (i, key, val) => {
    markTouched();
    setDrivers((p) => p.map((d, j) => (j === i ? { ...d, [key]: val || 0 } : d)));
  };

  /**
   * What the CRM baseline would fill in, held rather than applied.
   *
   * The choice of what to open on belongs in one place — see the seeding effect
   * below — and a fetch that writes straight into the inputs cannot participate
   * in that choice, it can only race it.
   */
  const [baselineDefaults, setBaselineDefaults] = useState(null);
  /**
   * The cheap half of the baseline, fetched every time.
   *
   * /sh/baseline answers in under a second and carries the three things the
   * engine cannot run correctly without: what this month has already closed
   * and installed, the closing-to-install lag, and the payment-type counts
   * behind the mix.
   *
   * Deferring it along with the eighteen-second summary read is what put 220
   * installs against a target of 110. Without the month's actuals the engine
   * has no idea that 73 of September's installs have already happened, so it
   * synthesises the full 110 on top of them — and drains the queue of eligible
   * projects, which is why October then reported 63 of 110 and blamed the lag.
   */
  const [monthState, setMonthState] = useState(null);

  /**
   * The last saved scenario, and a way to put it back.
   *
   * It used to load itself the moment it arrived, which left no way to tell
   * whether the numbers on screen were somebody's saved scenario or a fresh
   * calibration — they look identical, and only one of them reflects what the
   * pipeline is doing today. Loading it is now something you ask for, and the
   * header says what there is to load.
   */
  const savedSet = useMemo(() => {
    const first = months.map((m) => saved?.[m]).find((x) => x && x.assumptions);
    if (!first) return null;
    // A scenario saved by the other entity's model carries different driver
    // fields entirely, and seeding from it fills every box with undefined.
    if (first.assumptions.model && first.assumptions.model !== 'sh') return null;
    return {
      version: first.version,
      savedAt: first.savedAt,
      savedBy: first.savedBy,
      note: first.note,
      assumptions: first.assumptions,
      byMonth: saved || {},
    };
  }, [saved, months]);

  const applyAssumptions = (a) => {
    if (!a) return;
    if (a.byType) setByType(a.byType);
    if (a.avgClosingToInstallDays != null) setAvgClosingToInstallDays(a.avgClosingToInstallDays);
    if (a.includeExisting != null) setIncludeExisting(a.includeExisting);
  };

  /**
   * What the page opens on: the budget, if one has been agreed.
   *
   * A saved version is the forecast someone committed to, and that is what
   * should be on screen — not a fresh calibration that happens to describe
   * today's pipeline. Only where nothing has been saved for these months does
   * the CRM baseline fill the gap, and the header says which of the two you
   * are looking at.
   */
  useEffect(() => {
    if (touchedRef.current) return;
    if (savedSet) {
      applyAssumptions(savedSet.assumptions);
      setDrivers((prev) => months.map((m, i) => savedSet.byMonth?.[m]?.drivers || prev[i]));
      return;
    }
    if (baselineDefaults) {
      applyAssumptions(baselineDefaults);
      setDrivers(baselineDefaults.drivers);
    }
    // Deliberately not depending on drivers: this sets them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [touched, savedSet, baselineDefaults, months]);

  /**
   * Apply a scenario chosen in the drawer.
   *
   * Months the scenario never covered keep whatever is on screen: a scenario
   * that did not forecast December is not a scenario that forecast December at
   * zero, and blanking the row would assert the second.
   */
  useEffect(() => {
    if (!loadRequest?.scenario) return;
    const sc = loadRequest.scenario;
    markTouched();
    applyAssumptions(Object.values(sc.byMonth)[0]?.assumptions);
    setDrivers((prev) => months.map((m, i) => sc.byMonth[m]?.drivers || prev[i]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadRequest, months]);

  /**
   * How a month's total sales split across the three payment types.
   *
   * From the CRM where it has been read, from the saved scenario where it has
   * not, and only then from a default.
   *
   * The saved fallback is not a nicety. Deferring the CRM read left the mix on
   * its default of everything upfront, so a hundred systems that had been
   * saved as 61/17/22 came back as 100/0/0 — different revenue, different
   * margin, on a page whose whole claim is that it shows what was saved. The
   * scenario carries its own mix precisely so it can be reproduced without
   * asking the CRM again.
   */
  const mix = useMemo(() => {
    // The summary's counts where it has been read, else the cheap baseline's —
    // both are the same three months of closings, and the second costs a
    // second rather than eighteen.
    const count = (t) => baseline?.byType?.[t]?.count
      ?? monthState?.byType?.[t]?.count
      ?? 0;
    const total = TYPES.reduce((s, t) => s + count(t), 0);
    if (total) return Object.fromEntries(TYPES.map((t) => [t, count(t) / total]));

    const savedMix = savedSet?.assumptions?.mix;
    if (savedMix && TYPES.some((t) => savedMix[t] > 0)) {
      return Object.fromEntries(TYPES.map((t) => [t, Number(savedMix[t]) || 0]));
    }
    return { Upfront: 1, PPA: 0, BNPL: 0 };
  }, [baseline, monthState, savedSet]);

  const driversForEngine = useMemo(() => drivers.map((d) => {
    const total = d.totalSales || 0;
    const upfront = Math.round(total * mix.Upfront);
    const ppa = Math.round(total * mix.PPA);
    return {
      totalSales: total,
      salesUpfront: upfront,
      salesPPA: ppa,
      salesBNPL: Math.max(0, total - upfront - ppa),
      totalInstalls: d.totalInstalls || 0,
    };
  }), [drivers, mix]);

  /**
   * This month's actuals, the install lag, and the type counts.
   *
   * Always, and before anything else needs them — under a second, and the
   * engine is wrong without them rather than merely less informed.
   */
  const monthStateRef = useRef(null);
  const fetchMonthState = useCallback(async () => {
    try {
      const res = await api.get('/api/financial-planning/sh/baseline', {
        params: { month: dayjs(startMonth).startOf('month').format('YYYY-MM') },
      });
      monthStateRef.current = res.data.data;
      setMonthState(res.data.data);
    } catch (err) {
      setBaselineError(err.response?.data?.message
        || 'Could not read this month\u2019s closings and installs from the CRM');
    }
  }, [startMonth]);

  useEffect(() => { fetchMonthState(); }, [fetchMonthState]);

  const fetchBaseline = useCallback(async () => {
    setBaselineLoading(true);
    setBaselineError(null);
    try {
      const planMonthStart = dayjs(startMonth).startOf('month');
      const sumStart = planMonthStart.subtract(3, 'month').format('YYYY-MM-DD');
      const sumEnd = planMonthStart.subtract(1, 'day').format('YYYY-MM-DD');

      const summaryRes = await api.get('/api/retail-finance/summary', {
        params: { dateField: 'closingDate', start: sumStart, end: sumEnd },
      });
      // The cheap read has already happened, on its own schedule.
      const d = monthStateRef.current || {};

      /**
       * Per-type metrics from the retail finance summary rather than from Xero.
       *
       * Xero only knows what has been invoiced, and a project closed last month
       * has not been. The CRM's designed gross profit is available the day the
       * quote is accepted, which is the only figure that exists for the
       * projects this forecast is actually about.
       */
      const agg = Object.fromEntries(TYPES.map((t) => [t, {
        count: 0, sumRev: 0, sumPayment: 0, sumCrmGp: 0, gpCount: 0,
      }]));
      let totalCount = 0;
      for (const p of (summaryRes.data?.data?.projects || [])) {
        const pm = (p.paymentMethod || '').toLowerCase();
        const type = /ppa/.test(pm) ? 'PPA' : (/bnpl/.test(pm) ? 'BNPL' : 'Upfront');
        const t = agg[type];
        t.count += 1;
        t.sumRev += p.revenue || 0;
        t.sumPayment += p.payment || 0;
        if (p.gpMin != null && p.gpMax != null && (p.revenue || 0) > 0) {
          t.sumCrmGp += (p.gpMin + p.gpMax) / 2;
          t.gpCount += 1;
        }
        totalCount += 1;
      }
      const avg = (n, c) => (c ? Math.round(n / c) : 0);
      const rich = {
        ...d,
        systemCount: totalCount || d.systemCount,
        avgPerMonthCount: Math.round((totalCount || d.systemCount) / 3),
        byType: Object.fromEntries(TYPES.map((t) => [t, {
          count: agg[t].count,
          avgRevenue: avg(agg[t].sumRev, agg[t].count),
          avgCrmPayment: avg(agg[t].sumPayment, agg[t].count),
        }])),
      };
      setBaseline(rich);

      if (totalCount > 0) {
        setBaselineDefaults({
          byType: Object.fromEntries(TYPES.map((t) => {
            const gpPct = agg[t].gpCount > 0 && agg[t].sumRev > 0 ? agg[t].sumCrmGp / agg[t].sumRev : null;
            return [t, {
              avgRevenue: rich.byType[t].avgRevenue || 0,
              avgCrmPayment: rich.byType[t].avgCrmPayment || 0,
              cogsPct: gpPct != null ? Math.max(0, Math.min(1, 1 - gpPct)) : 0.75,
            }];
          })),
          avgClosingToInstallDays: d.avgClosingToInstallDays || 90,
          drivers: months.map((_, i) => {
            const act = (d.driverMonthActuals || [])[i] || { closings: 0, installs: 0 };
            const baseAvg = rich.avgPerMonthCount || 0;
            // Never below what the month has already done: a forecast of fewer
            // sales than have already closed is not a forecast.
            return {
              totalSales: Math.max(act.closings, baseAvg),
              totalInstalls: Math.max(act.installs, baseAvg),
            };
          }),
        });
      }
    } catch (err) {
      setBaselineError(err.response?.data?.message || 'Could not load the ShineHub baseline');
    }
    setBaselineLoading(false);
  }, [startMonth, months]);

  const fetchPipeline = useCallback(async () => {
    setPipelineLoading(true);
    try {
      const res = await api.get('/api/financial-planning/sh/pipeline', { params: { monthsBack } });
      setPipeline(res.data.data?.projects || []);
    } catch {
      setPipeline([]);
    }
    setPipelineLoading(false);
  }, [monthsBack]);

  /**
   * Only fetch what the screen actually needs.
   *
   * With a saved budget on screen, none of this is on it: the figures come
   * from the saved outputs, the drivers and assumptions from the saved
   * scenario. These reads exist to seed a model that has not been asked to run
   * yet — and they are Xero calls, several at once, which is what put the page
   * over Xero's five-concurrent limit and made it report the profit and loss
   * unavailable.
   *
   * So they wait until somebody touches something, which is the moment the
   * model has to produce numbers of its own.
   */
  const needsModel = !savedSet || touched;
  useEffect(() => { if (needsModel) fetchBaseline(); }, [needsModel, fetchBaseline]);
  useEffect(() => { fetchPipeline(); }, [fetchPipeline]);

  const forecast = useMemo(() => computeShForecast({
    existingPipeline: includeExisting ? pipeline : [],
    monthlyDrivers: driversForEngine,
    assumptions: { byType, avgClosingToInstallDays, monthlyOpex: 0 },
    // Opex is Opex Control's number, not this model's — it is passed as zero so
    // the engine's net profit is ignored rather than quietly disagreeing with
    // the budget the Opex tab inherits.
    openingBalances: {
      cash: 0, accountsReceivable: 0, inventory: 0, otherAssets: 0,
      accountsPayable: 0, otherLiabilities: 0, gstPayable: 0,
    },
    horizonMonths,
    startDate: dayjs(startMonth).startOf('month').format('YYYY-MM-DD'),
    today: dayjs().format('YYYY-MM-DD'),
    monthActuals: monthState?.driverMonthActuals || [],
    includeExistingActuals: includeExisting,
    // monthState, not baseline: the engine reads this month's actuals from it,
    // and leaving it out of the dependencies meant the forecast kept the
    // figures it had computed while they were still unknown.
  }), [pipeline, includeExisting, driversForEngine, byType, avgClosingToInstallDays,
    horizonMonths, startMonth, monthState]);

  const periods = useMemo(() => aggregateByPeriod(forecast.days, 'month'), [forecast]);

  /**
   * The same forecast, split by business line.
   *
   * Upfront and BNPL both land on the Upfront line and PPA lands on GTL, which
   * is how finance reads these: BNPL is the same product on the same terms from
   * the P&L's point of view, and a PPA system is sold through GTL.
   *
   * Derived from the resolved pipeline rather than from the engine's totals,
   * using the engine's own recognition dates — revenue at closing+7d for an
   * upfront sale and at install for everything else, cost at install for all of
   * them. Same projects, same dates, so the lines add back to the totals above
   * instead of being a second, nearly-equal answer.
   */
  const split = useMemo(() => {
    const start = dayjs(startMonth).startOf('month');
    const end = start.add(horizonMonths, 'month');
    const booked = {};
    const matched = {};
    const cell = (bag, month, line) => {
      if (!bag[month]) bag[month] = {};
      if (!bag[month][line]) bag[month][line] = { revenue: 0, cogs: 0, gp: 0 };
      return bag[month][line];
    };
    const within = (d) => d && !d.isBefore(start) && d.isBefore(end);
    const key = (d) => `${d.format('YYYY-MM')}-01`;

    for (const p of forecast.pipeline) {
      const line = p.paymentType === 'PPA' ? 'ppa' : 'upfront';
      const rev = p.revenue || 0;
      const cogsPct = p.cogsPct != null ? p.cogsPct : (byType[p.paymentType]?.cogsPct ?? 0.75);
      const cogs = rev * cogsPct;
      const install = p.installDate ? dayjs(p.installDate) : null;

      /**
       * As booked — the engine's own timing, which is Xero's: an upfront sale
       * invoices at closing and its cost arrives with the installer months
       * later. The two halves of one job land in different months.
       */
      const recog = p.paymentType === 'Upfront'
        ? (p.closingDate ? dayjs(p.closingDate).add(7, 'day') : null)
        : install;
      if (within(recog)) cell(booked, key(recog), line).revenue += rev;
      if (within(install)) cell(booked, key(install), line).cogs += cogs;

      /**
       * Matched — both halves at installation, which is when the job is
       * actually done. Same projects, same money, only the month differs; this
       * is the basis on which a monthly margin means anything.
       */
      if (within(install)) {
        const c = cell(matched, key(install), line);
        c.revenue += rev;
        c.cogs += cogs;
      }
    }

    for (const bag of [booked, matched]) {
      for (const month of Object.keys(bag)) {
        for (const line of Object.keys(bag[month])) {
          const c = bag[month][line];
          c.revenue = Math.round(c.revenue * 100) / 100;
          c.cogs = Math.round(c.cogs * 100) / 100;
          c.gp = Math.round((c.revenue - c.cogs) * 100) / 100;
        }
      }
    }
    return { booked, matched };
  }, [forecast, byType, startMonth, horizonMonths]);

  // Report upward. The page owns the comparison table; this owns the model.
  useEffect(() => {
    const byMonth = {};
    for (const p of periods) {
      const month = `${p.key}-01`;
      byMonth[month] = {
        revenue: p.pl.revenue,
        cogs: p.pl.cogs,
        gp: p.pl.gp,
        // The engine's own cash timing, so the statements do not have to fall
        // back on a flat collection percentage.
        cashIn: p.cf.cashIn,
        cashOutPurchases: p.cf.cashOutPurchases,
        byLine: split.booked[month] || null,
        /**
         * The same months on a matched basis, carried alongside rather than
         * instead. As-booked is what Xero will report and what the statements
         * have to tie to; matched is what the margin actually is. Saving keeps
         * both, so neither question has to be re-derived later.
         */
        matched: sumLines(split.matched[month]),
        matchedByLine: split.matched[month] || null,
      };
    }
    onForecast({
      byMonth,
      driversByMonth: Object.fromEntries(months.map((m, i) => [m, drivers[i] || {}])),
      assumptions: { model: 'sh', byType, avgClosingToInstallDays, includeExisting, mix },
      // Whether anyone has changed the inputs. The page needs it to decide
      // whether the figures on screen are still the saved budget.
      touched,
    });
  }, [periods, split, months, drivers, byType, avgClosingToInstallDays, includeExisting, mix,
    touched, onForecast]);

  const monthActuals = monthState?.driverMonthActuals || [];
  const pipelineByMonth = useMemo(() => months.map((m) => {
    const monthStart = dayjs(m).startOf('month').format('YYYY-MM-DD');
    const monthEnd = dayjs(m).endOf('month').format('YYYY-MM-DD');
    let closingToInstall = 0;
    let scheduled = 0;
    let installsCompleted = 0;
    let unpriced = 0;
    for (const p of forecast.pipeline) {
      if (p.installDate && p.installDate >= monthStart && p.installDate <= monthEnd) {
        installsCompleted += 1;
        // A backlog project the CRM has no price for contributes no revenue and
        // no cost. It still installs, so the count looks healthy while the
        // month's gross profit quietly comes out at nothing.
        if (!(p.revenue > 0)) unpriced += 1;
      }
      if (!p.closingDate || p.closingDate > monthEnd) continue;
      if (!p.installDate || p.installDate > monthEnd) {
        closingToInstall += 1;
        // Closed, not yet installed — but the CRM has already booked it a date
        // beyond this month, so the allocator never sees it. Counting it as
        // available backlog is what makes a shortfall look impossible.
        if (p.installDate && !p.installEstimated) scheduled += 1;
      }
    }
    const plan = (forecast.installPlan || []).find((x) => x.month === monthStart) || null;
    return { month: m, closingToInstall, scheduled, installsCompleted, unpriced, plan };
  }), [months, forecast]);

  const horizonStart = dayjs(startMonth).startOf('month');
  const horizonEnd = horizonStart.add(horizonMonths, 'month');
  const pipelineForDisplay = useMemo(() => forecast.pipeline.filter((p) => {
    if (!p.closingDate && !p.installDate) return false;
    if (!p.installDate) return true;
    const inst = dayjs(p.installDate);
    return inst.isAfter(horizonStart) && inst.isBefore(horizonEnd);
  }).slice(0, 500), [forecast, horizonStart, horizonEnd]);


/**
 * The model hands its sections back rather than laying them out.
 *
 * Cashflow Control interleaves them with its own output table — drivers, then
 * the result, then the assumptions behind it, then the pipeline — and a
 * component that renders its own column cannot be interleaved with anything.
 * Holding the state here and the order there keeps both where they belong.
 */
  const sections = {
    alerts: baselineError ? (
      <Alert type="warning" showIcon message={baselineError} />
    ) : null,
    assumptions: (
      <Card
              size="small"
              title="Assumptions — per payment type"
              extra={(
                <Space size={8} wrap>
                  {/* The values are already on screen — this only says a
                      background read is still running, rather than masking
                      figures the page already has. */}
                  {baselineLoading && savedSet && (
                    <span style={{ fontSize: 11, color: NEUTRAL }}>
                      <Spin size="small" style={{ marginRight: 6 }} />
                      reading the CRM…
                    </span>
                  )}
                  {savedSet ? (
                    <>
                      <Tag color="blue" style={{ margin: 0 }}>
                        showing v{savedSet.version}, saved {dayjs(savedSet.savedAt).format('D MMM')}
                        {savedSet.savedBy ? ` by ${savedSet.savedBy}` : ''}
                      </Tag>
                      <Tooltip title="Choose a saved scenario, read what is in it, then load it.">
                        <Button size="small" onClick={onOpenScenarios}>load saved</Button>
                      </Tooltip>
                      <span style={{ borderLeft: '1px solid #e6ebf1', height: 16 }} />
                    </>
                  ) : (
                    <Tag style={{ margin: 0 }}>
                      nothing saved — showing the last 3 months&apos; average
                    </Tag>
                  )}
                  <span style={{ fontSize: 12, color: NEUTRAL }}>Existing pipeline</span>
                  <Switch
                    size="small"
                    checked={includeExisting}
                    onChange={(v) => { markTouched(); setIncludeExisting(v); }}
                  />
                  <span style={{ fontSize: 11, color: NEUTRAL }}>
                    {includeExisting ? 'existing + new sales' : 'new sales only'}
                  </span>
                </Space>
              )}
            >
              <Spin spinning={baselineLoading && !savedSet}>
                <Table
                  size="small"
                  pagination={false}
                  dataSource={TYPES.map((type) => ({
                    key: type, type, ...byType[type], baselineCount: baseline?.byType?.[type]?.count || 0,
                  }))}
                  columns={[
                    {
                      title: 'Type', dataIndex: 'type', width: 90,
                      render: (v) => <Tag color={typeColour(v)}>{v}</Tag>,
                    },
                    {
                      title: 'Payment terms', dataIndex: 'type', width: 300,
                      render: (v) => (
                        <span style={{ fontSize: 12, color: NEUTRAL }}>
                          {v === 'Upfront'
                            ? '10% at closing+7d · 80% at install−14d · balance at install+14d'
                            : 'Revenue at install · payment at install+14d'}
                        </span>
                      ),
                    },
                    {
                      title: 'GP %', dataIndex: 'cogsPct', width: 120,
                      render: (v, rec) => (
                        <InputNumber
                          value={Math.round((1 - v) * 1000) / 10}
                          onChange={(val) => updateType(rec.type, 'cogsPct', Math.max(0, Math.min(1, 1 - (val || 0) / 100)))}
                          style={{ width: '100%' }}
                          min={0} max={100} step={0.1}
                          formatter={(x) => `${x}%`} parser={(x) => x.replace('%', '')}
                        />
                      ),
                    },
                    {
                      title: 'Avg revenue / system', dataIndex: 'avgRevenue', width: 170,
                      render: (v, rec) => (
                        <InputNumber
                          value={v} onChange={(val) => updateType(rec.type, 'avgRevenue', val || 0)}
                          style={{ width: '100%' }} formatter={moneyFmt} parser={moneyParse}
                        />
                      ),
                    },
                    {
                      title: 'Avg CRM payment / system', dataIndex: 'avgCrmPayment', width: 180,
                      render: (v, rec) => (
                        <InputNumber
                          value={v} onChange={(val) => updateType(rec.type, 'avgCrmPayment', val || 0)}
                          style={{ width: '100%' }} formatter={moneyFmt} parser={moneyParse}
                        />
                      ),
                    },
                    {
                      title: 'Past 3-mo', dataIndex: 'baselineCount', width: 100, align: 'right',
                      render: (v) => <span style={{ color: NEUTRAL }}>{v} systems</span>,
                    },
                  ]}
                />
                <Row gutter={[16, 12]} style={{ marginTop: 14 }}>
                  <Col xs={24} md={8}>
                    <div style={{ fontSize: 12, color: NEUTRAL, marginBottom: 4 }}>Lag days (closing → install)</div>
                    <InputNumber
                      value={avgClosingToInstallDays}
                      onChange={(v) => { markTouched(); setAvgClosingToInstallDays(v || 0); }}
                      min={0} max={365} addonAfter="d" style={{ width: 140 }}
                    />
                    <div style={{ fontSize: 11, color: NEUTRAL, marginTop: 4 }}>
                      minimum lag before the install queue will assign a date
                    </div>
                  </Col>
                  <Col xs={24} md={16}>
                    {/* The mix shows whether or not the CRM has been read, and
                        says which it is. It decides how a month's total sales
                        split, so a reader who sees 61/17/22 become 100/0/0
                        should be able to find out why without guessing. */}
                    <div style={{ fontSize: 11, color: NEUTRAL, marginTop: 20 }}>
                      {(baseline?.systemCount || monthState?.systemCount) > 0 ? (
                        <>
                          Past 3 months closed:{' '}
                          {baseline?.systemCount || monthState?.systemCount} systems · mix{' '}
                        </>
                      ) : 'Mix '}
                      {TYPES.map((t) => `${Math.round(mix[t] * 1000) / 10}% ${t}`).join(' / ')}
                      {!baseline && !monthState?.byType && (savedSet
                        ? ' — as saved with this scenario'
                        : ' — no CRM history read, so all sales are treated as upfront')}
                    </div>
                  </Col>
                </Row>
              </Spin>
            </Card>
    ),
    drivers: (
      <Card size="small" title="Drivers — systems per month">
              <Spin spinning={baselineLoading && !savedSet}>
                <Table
                  size="small"
                  pagination={false}
                  dataSource={drivers.map((d, i) => ({
                    key: i,
                    ...d,
                    ...(driversForEngine[i] || {}),
                    monthLabel: dayjs(months[i]).format('MMM YYYY'),
                    actualClosings: (monthActuals[i] || {}).closings || 0,
                    actualInstalls: (monthActuals[i] || {}).installs || 0,
                  }))}
                  columns={[
                    { title: 'Month', dataIndex: 'monthLabel', width: 110, fixed: 'left' },
                    {
                      title: 'Total sales', dataIndex: 'totalSales', width: 140,
                      render: (v, rec) => {
                        const floor = includeExisting ? rec.actualClosings : 0;
                        return (
                          <div>
                            <InputNumber
                              value={v} min={floor} style={{ width: '100%' }}
                              onChange={(val) => updateDriver(rec.key, 'totalSales', Math.max(floor, val || 0))}
                            />
                            {includeExisting && rec.actualClosings > 0 && (
                              <div style={{ fontSize: 11, color: NEUTRAL, marginTop: 2 }}>actual MTD: {rec.actualClosings}</div>
                            )}
                          </div>
                        );
                      },
                    },
                    ...TYPES.map((t) => ({
                      title: <Tag color={typeColour(t)}>{t}</Tag>,
                      dataIndex: `sales${t === 'Upfront' ? 'Upfront' : t}`,
                      width: 90,
                      align: 'right',
                      render: (v) => <span style={{ color: NEUTRAL }}>{v}</span>,
                    })),
                    {
                      title: 'Total installs', dataIndex: 'totalInstalls',
                      render: (v, rec) => {
                        const floor = includeExisting ? rec.actualInstalls : 0;
                        return (
                          <div>
                            <InputNumber
                              value={v} min={floor} style={{ width: 150 }}
                              onChange={(val) => updateDriver(rec.key, 'totalInstalls', Math.max(floor, val || 0))}
                            />
                            {includeExisting && rec.actualInstalls > 0 && (
                              <div style={{ fontSize: 11, color: NEUTRAL, marginTop: 2 }}>actual MTD: {rec.actualInstalls}</div>
                            )}
                          </div>
                        );
                      },
                    },
                  ]}
                />
                {/* An install target the queue cannot meet is the most useful thing
                    this model says, and it is easy to miss in the output table. */}
                <div style={{ fontSize: 12, color: NEUTRAL, marginTop: 10 }}>
                  {pipelineByMonth.map((c, i) => {
                    const target = Number(drivers[i]?.totalInstalls) || 0;
                    const short = c.installsCompleted < target;
                    const free = c.closingToInstall - c.scheduled;
                    return (
                      <div key={c.month} style={{ marginBottom: 2 }}>
                        <b>{dayjs(c.month).format('MMM YYYY')}:</b>
                        {' '}closing→install{' '}
                        <Tooltip title={c.scheduled > 0
                          ? `${c.closingToInstall} closed and not yet installed, but ${c.scheduled} of them already carry an install date from the CRM. Only the other ${free} are the allocator's to place.`
                          : 'Closed and not yet installed at the end of this month.'}
                        >
                          <span style={{ borderBottom: '1px dotted #bbb' }}>
                            {c.closingToInstall}
                            {c.scheduled > 0 && ` (${free} unscheduled)`}
                          </span>
                        </Tooltip>
                        {' · installs placed '}
                        <span style={{ color: short ? '#a8071a' : '#146c43', fontWeight: 600 }}>
                          {c.installsCompleted}/{target}
                        </span>
                        {short && (
                          /* Which of the two limits actually bound, in the
                             allocator's own words — and for the lag, the
                             closing date a project had to beat to be in with a
                             chance, because that is the number a planner can
                             act on. */
                          <span style={{ color: '#a8071a', marginLeft: 6 }}>
                            {c.plan?.reason === 'lag' ? (
                              <>target unmet — only {c.plan.eligible} of the {c.plan.waiting} unscheduled
                                {' '}projects closed by {dayjs(c.plan.cutoff).format('D MMM')}, the last date
                                {' '}a sale can still install this month at a {avgClosingToInstallDays}d lag
                              </>
                            ) : c.plan?.reason === 'queue-empty' ? (
                              <>target unmet — the queue ran dry; {avgClosingToInstallDays}d of sales
                                {' '}at this rate cannot feed {target} installs a month
                              </>
                            ) : (
                              <>target unmet — the {avgClosingToInstallDays}d lag caps what can be installed</>
                            )}
                          </span>
                        )}
                        {c.unpriced > 0 && (
                          <Tooltip title="Project revenue comes from the CRM's GST figure. A project with none contributes no revenue and no cost, so it installs without ever reaching the P&L.">
                            <span style={{ color: '#d48806', marginLeft: 6 }}>
                              · {c.unpriced} of them have no price in the CRM and earn nothing
                            </span>
                          </Tooltip>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Spin>
            </Card>
    ),
    pipeline: (
      <Card
              size="small"
              title={(
                <span>
                  Project pipeline
                  <Tag color="blue" style={{ marginLeft: 8 }}>{pipelineForDisplay.length} shown</Tag>
                  <Tag style={{ marginLeft: 4 }}>
                    {forecast.pipeline.length} total ({includeExisting ? pipeline.length : 0} real
                    {' + '}{forecast.pipeline.length - (includeExisting ? pipeline.length : 0)} forecast)
                  </Tag>
                </span>
              )}
              extra={(
                <Space>
                  <span style={{ fontSize: 12, color: NEUTRAL }}>Closing within last</span>
                  <Select
                    value={monthsBack} onChange={setMonthsBack} size="small" style={{ width: 110 }}
                    options={[3, 6, 12, 24].map((v) => ({ value: v, label: `${v} months` }))}
                  />
                </Space>
              )}
            >
              <Spin spinning={pipelineLoading}>
                <Table
                  size="small"
                  rowKey={(p) => p.id || `${p.name}:${p.closingDate}`}
                  dataSource={pipelineForDisplay}
                  pagination={{ defaultPageSize: 20, size: 'small', showSizeChanger: true }}
                  scroll={{ x: 900 }}
                  columns={[
                    {
                      title: 'Name', dataIndex: 'name', width: 200, ellipsis: true,
                      render: (v, rec) => (rec.synthetic ? <span style={{ color: NEUTRAL }}>{v}</span> : v),
                    },
                    {
                      title: 'Type', dataIndex: 'paymentType', width: 90,
                      render: (v) => <Tag color={typeColour(v)}>{v}</Tag>,
                    },
                    {
                      title: 'CRM revenue', dataIndex: 'revenue', width: 130, align: 'right',
                      render: (v) => `$${Math.round(v || 0).toLocaleString()}`,
                    },
                    {
                      title: 'GP', width: 110, align: 'right',
                      render: (_, rec) => {
                        const pct = rec.cogsPct != null ? rec.cogsPct : (byType[rec.paymentType]?.cogsPct ?? 0.75);
                        return `$${Math.round((rec.revenue || 0) * (1 - pct)).toLocaleString()}`;
                      },
                    },
                    {
                      title: 'GP %', width: 80, align: 'right',
                      render: (_, rec) => {
                        const pct = rec.cogsPct != null ? rec.cogsPct : (byType[rec.paymentType]?.cogsPct ?? 0.75);
                        return `${Math.round((1 - pct) * 1000) / 10}%`;
                      },
                    },
                    {
                      title: 'Closing', dataIndex: 'closingDate', width: 120,
                      render: (v, rec) => (v
                        ? (
                          <span style={rec.closingEstimated ? { color: NEUTRAL, fontStyle: 'italic' } : {}}>
                            {dayjs(v).format('D MMM YY')}{rec.closingEstimated ? ' *' : ''}
                          </span>
                        )
                        : '—'),
                    },
                    {
                      title: 'Install', dataIndex: 'installDate', width: 130,
                      render: (v, rec) => (v
                        ? (
                          <span style={rec.installEstimated ? { color: NEUTRAL, fontStyle: 'italic' } : {}}>
                            {dayjs(v).format('D MMM YY')}{rec.installEstimated ? ' *' : ''}
                          </span>
                        )
                        : <span style={{ color: '#bbb' }}>(in pipeline)</span>),
                    },
                  ]}
                />
                <div style={{ fontSize: 11, color: NEUTRAL, marginTop: 8 }}>
                  <i>* estimated — a synthetic project, or a real one whose install date the queue assigned.</i>
                </div>
              </Spin>
            </Card>
    ),
  };

  if (renderSections) return renderSections(sections);
  return (
    <Space direction="vertical" size={14} style={{ width: '100%' }}>
      {sections.alerts}
      {sections.assumptions}
      {sections.drivers}
      {sections.pipeline}
    </Space>
  );
}
